import Facade from '@/Facade'
import { IModel } from '@/model/IModel'
import GameSignals from '@/signals/GameSignals'
import StateSignals from '@/stateMachine/StateSignals'
import { GameStatesEnum } from '@/states/GameStatesEnum'

interface AutoplaySettings {
    singleWin?: number
    balanceLower?: number
    balanceHigher?: number
    balanceLowerEnabled?: boolean
    balanceHigherEnabled?: boolean
    stopOnAnyWin?: boolean
}

export class AutoplayManager {
    private static _instanceSeq = 0
    private _model: IModel
    private _currentState: string
    private _left: number
    private _total: number
    private _autoplaySettings: AutoplaySettings
    private _autoPlayBalance: number
    private _isPaused: boolean
    private _hasProcessedSpinSignalForCurrentRound: boolean
    private readonly _instanceId: number

    public constructor(model: IModel) {
        this._instanceId = ++AutoplayManager._instanceSeq
        this._isPaused = false
        this._hasProcessedSpinSignalForCurrentRound = false
        this._model = model
        this._currentState = ''

        console.log(`[AutoplayManager#${this._instanceId}] ctor`)

        GameSignals.gamePause.connect(this.onGamePause)
        StateSignals.stateChanged.connect(this.onStateChanged)
        GameSignals.startAutoplay.connect(this.onStartAutoplay)
        GameSignals.setAutoplaySettings.connect(this.onSetAutoplaySettings)
        GameSignals.stopAutoplay.connect(this.onStopAutoplay)

        this._autoplaySettings = {
            singleWin: undefined,
            balanceHigher: undefined,
            balanceLower: undefined,
            balanceLowerEnabled: true,
            balanceHigherEnabled: true,
            stopOnAnyWin: false,
        }
    }

    private onGamePause = () => {
        Facade.isAuto = false

        this._isPaused = true
        this._total = 0
        this._left = 0
    }

    private onStateChanged = (state: string) => {
        this._currentState = state

        if (this._currentState === GameStatesEnum.START_ROUND_STATE) {
            console.log(`[AutoplayManager#${this._instanceId}] state=START_ROUND`, {
                isAuto: Facade.isAuto,
                leftBefore: this._left,
                total: this._total,
                processedRound: this._hasProcessedSpinSignalForCurrentRound,
            })
            this._hasProcessedSpinSignalForCurrentRound = false
            this.processAutoplayRoundStart()
        }

        if (this._currentState === GameStatesEnum.STOP_SPIN_STATE) {
            console.log(`[AutoplayManager#${this._instanceId}] state=STOP_SPIN`, {
                isAuto: Facade.isAuto,
                left: this._left,
                total: this._total,
            })
            this._hasProcessedSpinSignalForCurrentRound = false
        }

        if (this._currentState === GameStatesEnum.STOP_SPIN_STATE) {
            if (this._left > 0) {
                if (this.checkConditions()) {
                    GameSignals.stopAutoplay.emit()
                    Facade.isAuto = false
                    this.resetSettings()
                }
            }
        }
    }

    private processAutoplayRoundStart = () => {
        if (!Facade.isAuto || this._model.roundState.isFreeSpins) {
            console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:skip`, {
                reason: !Facade.isAuto ? 'not-auto' : 'free-spins',
                left: this._left,
                total: this._total,
            })
            return
        }

        if (this._hasProcessedSpinSignalForCurrentRound) {
            console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:skip`, {
                reason: 'already-processed-round',
                left: this._left,
                total: this._total,
            })
            return
        }

        this._hasProcessedSpinSignalForCurrentRound = true

        const leftBefore = this._left

        if (this._total > 0) {
            this._left = Math.max(0, this._left - 1)
        }

        console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:decrement`, {
            leftBefore,
            leftAfter: this._left,
            total: this._total,
        })

        if (this._left === 0) {
            console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:emit stopAutoplay`)
            GameSignals.stopAutoplay.emit()
            return
        }

        console.log(`[AutoplayManager#${this._instanceId}] processRoundStart:emit updateAutoplayValue`, {
            value: this._left,
        })
        GameSignals.updateAutoplayValue.emit(this._left)
    }

    private onStartAutoplay = (value: number) => {
        Facade.isAuto = true
        this._hasProcessedSpinSignalForCurrentRound = false

        const normalizedValue = Number(value)
        this._total = Number.isFinite(normalizedValue) && normalizedValue > 0 ? Math.floor(normalizedValue) : 0
        this._left = this._total
        this._autoPlayBalance = Number(this._model.balance)

        console.log(`[AutoplayManager#${this._instanceId}] onStartAutoplay`, {
            requested: value,
            normalized: this._total,
            left: this._left,
            state: this._currentState,
        })

        if (
            this._currentState === GameStatesEnum.IDLE_STATE ||
            this._currentState === GameStatesEnum.LINE_BY_LINE_STATE
        ) {
            GameSignals.spinButtonClicked.emit()
        }
    }

    private onSetAutoplaySettings = (settings: AutoplaySettings) => {
        const parseLimit = (value: unknown): number | undefined => {
            if (value === null || value === undefined || value === '') return undefined
            const parsed = Number(value)
            if (!Number.isFinite(parsed) || parsed < 0) return undefined
            return parsed
        }

        if ('singleWin' in settings) {
            this._autoplaySettings.singleWin = parseLimit(settings.singleWin)
        }

        if ('balanceLower' in settings) {
            this._autoplaySettings.balanceLower = parseLimit(settings.balanceLower)
        }

        if ('balanceHigher' in settings) {
            this._autoplaySettings.balanceHigher = parseLimit(settings.balanceHigher)
        }

        if ('balanceLowerEnabled' in settings && settings.balanceLowerEnabled !== undefined) {
            this._autoplaySettings.balanceLowerEnabled = !!settings.balanceLowerEnabled
        }

        if ('balanceHigherEnabled' in settings && settings.balanceHigherEnabled !== undefined) {
            this._autoplaySettings.balanceHigherEnabled = !!settings.balanceHigherEnabled
        }

        if ('stopOnAnyWin' in settings && settings.stopOnAnyWin !== undefined) {
            this._autoplaySettings.stopOnAnyWin = !!settings.stopOnAnyWin
        }

        this._autoPlayBalance = Number(this._model.balance)
    }

    private onStopAutoplay = () => {
        console.log(`[AutoplayManager#${this._instanceId}] onStopAutoplay`, {
            leftBefore: this._left,
            totalBefore: this._total,
            state: this._currentState,
        })
        Facade.isAuto = false
        this._hasProcessedSpinSignalForCurrentRound = false

        this._total = 0
        this._left = 0
    }

    private getCurrentSessionBalance = (): number => {
        const spinBalance = Number(this._model?.spinData?.balance)
        if (Number.isFinite(spinBalance)) {
            return spinBalance
        }

        const modelBalance = Number(this._model?.balance)
        return Number.isFinite(modelBalance) ? modelBalance : 0
    }

    private checkConditions(): boolean {
        if (!this._model.spinData) {
            return false
        }

        if (
            this._autoplaySettings.balanceHigherEnabled !== false &&
            this._autoplaySettings.balanceHigher &&
            Number.isFinite(this._autoPlayBalance) &&
            this._model.spinData.balance >
                this._autoPlayBalance + (this._autoPlayBalance * this._autoplaySettings.balanceHigher) / 100
        ) {
            return true
        }

        if (
            this._autoplaySettings.balanceLowerEnabled !== false &&
            this._autoplaySettings.balanceLower &&
            Number.isFinite(this._autoPlayBalance) &&
            this.getCurrentSessionBalance() <= this._autoPlayBalance - this._autoplaySettings.balanceLower
        ) {
            return true
        }

        if (this._autoplaySettings.stopOnAnyWin && this._model.spinData.win_currency > 0) {
            return true
        }

        if (
            this._autoplaySettings.singleWin !== undefined &&
            this._model.spinData.win_currency >= this._autoplaySettings.singleWin
        ) {
            return true
        }

        return false
    }

    private resetSettings() {
        this._autoplaySettings.balanceHigher = undefined
        this._autoplaySettings.balanceLower = undefined
        this._autoplaySettings.singleWin = undefined
        this._autoplaySettings.balanceLowerEnabled = true
        this._autoplaySettings.balanceHigherEnabled = true
        this._autoplaySettings.stopOnAnyWin = false
        this._autoPlayBalance = undefined

        this._left = 0
    }
}
